import { useCallback } from 'react';
import type { Logger } from '@archipro-website/logger';
import type { Tracker } from '../../types';
import {
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PRODUCT_ID,
    DOWNLOAD_ATTR_PROJECT_ID,
    DOWNLOAD_ATTR_FILE_ID,
    DOWNLOAD_ATTR_SURFACE,
} from '../../constants';
import type { TrackedUser } from '../../trackers/trackedUser';
import { buildDownloadsClickExtraData } from '../../utils/buildDownloadsClickExtraData';

/**
 * Prefer the browser-resolved absolute href on anchors; fall back to the raw
 * attribute for non-anchor targets so relative destinations stay relative.
 */
const resolveDownloadFileUrl = (target: HTMLElement): string => {
    if (target instanceof HTMLAnchorElement) {
        return target.href;
    }

    return target.getAttribute('href') ?? '';
};

/**
 * Return a function that tracks download clicks, which fires the
 * `DownloadsClick` event. Only tracks the click if the element has the correct
 * attributes set (`DOWNLOAD_ATTR_PROFESSIONAL_TITLE`, `DOWNLOAD_ATTR_FILE_TITLE`).
 * ExtraData carries `fileTitle`, `fileUrl`, `isExternal`, and `MouseButton` for
 * Pixel session Event Details / EVENT_DETAILS_URL.
 * @param trackerInstance The tracker instance.
 * @param logger The logger instance.
 * @param user The logged-in or guest user.
 * @param onTrack Callback when the `DownloadsClick` event is tracked.
 */
export const useTrackDownloadClick = (
    trackerInstance: Tracker,
    logger: Logger,
    user?: TrackedUser,
    onTrack?: (data: {
        professionalTitle: string;
        fileTitle: string;
        fileID?: number;
        professionalID?: number;
        productID?: number;
        projectID?: number;
    }) => void,
) => {
    return useCallback(
        (target: HTMLElement, mouseButton: 0 | 1 | 2) => {
            const professionalTitle = target.getAttribute(
                DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
            );
            const professionalIDAttr = target.getAttribute(
                DOWNLOAD_ATTR_PROFESSIONAL_ID,
            );
            const productIDAttr = target.getAttribute(DOWNLOAD_ATTR_PRODUCT_ID);
            const projectIDAttr = target.getAttribute(DOWNLOAD_ATTR_PROJECT_ID);
            const fileTitle = target.getAttribute(DOWNLOAD_ATTR_FILE_TITLE);
            const fileIDAttr = target.getAttribute(DOWNLOAD_ATTR_FILE_ID);
            const surface = target.getAttribute(DOWNLOAD_ATTR_SURFACE);

            if (!professionalTitle) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning(
                    `Missing attribute: ${DOWNLOAD_ATTR_PROFESSIONAL_TITLE}. Tracking performance will be degraded.`,
                );
                return;
            }
            if (!fileTitle) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                logger.warning(
                    `Missing attribute: ${DOWNLOAD_ATTR_FILE_TITLE}. Tracking performance will be degraded.`,
                );
                return;
            }

            const professionalID = professionalIDAttr
                ? parseInt(professionalIDAttr) || 0
                : undefined;
            const productID = productIDAttr
                ? parseInt(productIDAttr) || 0
                : undefined;
            const projectID = projectIDAttr
                ? parseInt(projectIDAttr) || 0
                : undefined;
            const fileID = fileIDAttr ? parseInt(fileIDAttr) || 0 : undefined;
            const fileUrl = resolveDownloadFileUrl(target);

            void trackerInstance.log('DownloadsClick', {
                url: new URL(window.location.href),
                label: professionalTitle,
                data: {
                    ExtraData: buildDownloadsClickExtraData({
                        fileTitle,
                        fileUrl,
                        MouseButton: mouseButton,
                    }),
                    ...(professionalID && { professionalID }),
                    ...(productID && { productID }),
                    ...(projectID && { projectID }),
                    // The keys the warehouse keeps; the lower-case ones above
                    // feed GA4 and the ad trackers.
                    ...((productID || projectID) && {
                        LibraryItemID: productID || projectID,
                    }),
                    ...(professionalID && { ProfessionalID: professionalID }),
                    ...(fileID && { fileID }),
                    fileTitle,
                    // Matches the gated path, which reports the surface as
                    // Source — so a tear sheet or lookbook taken by an
                    // entitled reader is still separable from a supplier file.
                    ...(surface && { Source: surface }),
                    memberID: user?.ID || 0,
                    ...(user?.__typename && {
                        memberType: user.__typename,
                    }),
                },
            });

            onTrack?.({
                professionalTitle,
                fileTitle,
                fileID,
                professionalID,
                productID,
                projectID,
            });
        },
        [trackerInstance, logger, user, onTrack],
    );
};
